import React, { useRef } from 'react';
import Portal from '../Portal/Portal';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { ElementSize } from '../../types/element-size';
import { ButtonType } from '../../types/button-type';
import IconCrossRound from '../Icons/IconCrossRound/IconCrossRound';

export type ModalProps = {
  acceptBtnText?: string;
  cancelBtnText?: string;
  children?: React.ReactNode;
  className?: string;
  content?: string;
  isMobile?: boolean;
  isVisible: boolean;
  modalTitle: string;
  onClose: (data: any) => void;
  onAccept?: (data: any) => void;
  showAcceptBtn?: boolean;
  showCancelBtn?: boolean;
  showCrossRoundIcon?: boolean;
  acceptBtnDisabled?: boolean;
  cancelBtnDisabled?: boolean;
};

const Modal: React.FC<ModalProps> = ({
  acceptBtnText = 'Create',
  cancelBtnText = 'Cancel',
  children,
  className = '',
  content,
  isMobile = false,
  isVisible,
  modalTitle,
  onClose,
  onAccept,
  showAcceptBtn = true,
  showCancelBtn = true,
  showCrossRoundIcon = true,
  acceptBtnDisabled = false,
  cancelBtnDisabled = false,
}) => {

  const modalClassName = !isMobile ? `c-modal overflow-auto flex flex-col relative w-full h-full sm:max-h-fit sm:max-w-100 2xl:max-w-142.5  sm:rounded-lg bg-white px-8 py-12 text-center dark:bg-boxdark 2xl:px-17.5 2xl:py-15 ${className}` : `c-modal mobile ${className}`;
  const modalOverlayClassName = !isMobile ? 'c-modal__overlay fixed left-0 top-0 z-9999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 sm:px-4 sm:py-5' : 'c-modal__overlay mobile';

  // Close only when both the mousedown AND the mouseup happened directly on
  // the overlay itself - not merely when the click event's resolved target
  // happens to be the overlay. A click/drag that starts inside the modal
  // (e.g. selecting text in an input) and is released outside its bounds
  // fires its "click" on the overlay too (the nearest common ancestor of the
  // mousedown/mouseup targets), which would otherwise close the modal.
  const isOverlayMouseDown = useRef(false);

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    isOverlayMouseDown.current = e.target === e.currentTarget;
  };

  const handleOverlayMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    const shouldClose = isOverlayMouseDown.current && e.target === e.currentTarget;
    isOverlayMouseDown.current = false;

    if (shouldClose) {
      onClose(e);
    }
  };

  return (
    <Portal>
      {isVisible && (
        <div
          className={modalOverlayClassName}
          onMouseDown={handleOverlayMouseDown}
          onMouseUp={handleOverlayMouseUp}
        >
          <div
            className={modalClassName}
          >
            {showCrossRoundIcon && <IconCrossRound fill='#FF0000' onClick={onClose} width='100%' height='100%' iconClassName='absolute h-6 w-6 2xl:w-8 2xl:h-8 right-4 top-4'/>}
            <div className="c-modal__header">
              <h3 className="pb-2 text-xl font-bold text-black dark:text-white sm:text-xl 2xl:text-2xl">
                {modalTitle}
              </h3>
              <span className="mx-auto mb-6 inline-block h-1 w-22.5 rounded bg-primary"></span>
            </div>
            <div className="c-modal__content flex-auto">
              {children || (
                <p className='text-lg'>{content}</p>
              )}
            </div>
            {(showAcceptBtn || showCancelBtn ) &&
              <div className="c-modal__footer w-full flex flex-row justify-between pt-4">
                {showAcceptBtn && (
                  <InteractiveButton
                    onClick={onClose}
                    text={cancelBtnText}
                    size={ElementSize.XL}
                    type={ButtonType.PrimaryInverse}
                    disabled={cancelBtnDisabled}
                  />
                )}
                {showCancelBtn && (
                  <InteractiveButton
                    onClick={onAccept}
                    text={acceptBtnText}
                    size={ElementSize.XL}
                    disabled={acceptBtnDisabled}
                  />
                )}
              </div>
            }
          </div>
        </div>
      )}
    </Portal>
  );
};

export default Modal;
